import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  api,
  type Client,
  type ProjectListEntry,
  type ProjectPatchInput,
} from '../lib/api';
import { parseRateInput, projectDisplayName, rateInputValue } from '../lib/format';
import { Modal } from './Modal';

interface Props {
  open: boolean;
  project: ProjectListEntry;
  clients: Client[];
  allProjects: ProjectListEntry[];
  onClose: () => void;
  onSaved: (updated: ProjectListEntry) => void;
}

// Returns the set of project_keys we must NOT allow as parent of `project`:
// `project` itself (self), and every descendant of `project`. Walking the
// tree downward keeps cycle prevention out of the user's reach in UI; the
// Worker enforces it server-side either way.
function collectForbiddenParents(
  project: ProjectListEntry,
  all: ProjectListEntry[]
): Set<string> {
  const byParentKey = new Map<string, ProjectListEntry[]>();
  const idToKey = new Map<string, string>();
  for (const p of all) {
    idToKey.set(p.project_key, p.project_key);
    if (p.parent_project_key) {
      const arr = byParentKey.get(p.parent_project_key) ?? [];
      arr.push(p);
      byParentKey.set(p.parent_project_key, arr);
    }
  }
  const forbidden = new Set<string>([project.project_key]);
  const queue: ProjectListEntry[] = [project];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    const children = byParentKey.get(cur.project_key) ?? [];
    for (const c of children) {
      if (!forbidden.has(c.project_key)) {
        forbidden.add(c.project_key);
        queue.push(c);
      }
    }
  }
  return forbidden;
}

export function ProjectEditModal({
  open,
  project,
  clients,
  allProjects,
  onClose,
  onSaved,
}: Props) {
  const [displayName, setDisplayName] = useState('');
  const [clientId, setClientId] = useState<string | null>(null);
  const [parentProjectId, setParentProjectId] = useState<string | null>(null);
  const [tagsInput, setTagsInput] = useState('');
  const [standardRate, setStandardRate] = useState('');
  const [humanRate, setHumanRate] = useState('');
  const [agentRate, setAgentRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parentOptions = useMemo(() => {
    const forbidden = collectForbiddenParents(project, allProjects);
    return allProjects
      .filter((p) => !forbidden.has(p.project_key))
      .sort((a, b) =>
        projectDisplayName(a).localeCompare(projectDisplayName(b))
      );
  }, [project, allProjects]);

  useEffect(() => {
    if (!open) return;
    setDisplayName(project.display_name ?? '');
    setClientId(project.client_id);
    setParentProjectId(project.parent_project_id);
    setTagsInput(project.tags.join(', '));
    setStandardRate(rateInputValue(project.standard_rate_cents));
    setHumanRate(rateInputValue(project.human_rate_cents));
    setAgentRate(rateInputValue(project.agent_rate_cents));
    setError(null);
  }, [open, project]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const tags = tagsInput
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

      const patch: ProjectPatchInput = {
        display_name: displayName.trim().length > 0 ? displayName.trim() : null,
        client_id: clientId,
        parent_project_id: parentProjectId,
        tags,
        standard_rate_cents: parseRateInput(standardRate),
        human_rate_cents: parseRateInput(humanRate),
        agent_rate_cents: parseRateInput(agentRate),
      };

      const updated = await api.patchProject(project.project_key, patch);
      onSaved(updated);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit project"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="submit"
            form="project-edit-form"
            disabled={saving}
            className="btn-primary"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form id="project-edit-form" onSubmit={handleSubmit} className="space-y-5">
        <Field label="Friendly name" hint="Replaces the folder basename in the UI">
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={project.name}
            maxLength={128}
            className="input"
          />
        </Field>

        <Field
          label="Parent project"
          hint="Group this project under another (e.g. monorepo apps)"
        >
          <select
            value={parentProjectId ?? ''}
            onChange={(e) => setParentProjectId(e.target.value || null)}
            className="input font-mono text-[12px]"
          >
            <option value="">— none —</option>
            {parentOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {projectDisplayName(p)}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Client">
          <select
            value={clientId ?? ''}
            onChange={(e) => setClientId(e.target.value || null)}
            className="input"
          >
            <option value="">— none —</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Tags" hint="Comma-separated">
          <input
            type="text"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="urgent, q4, internal"
            className="input"
          />
        </Field>

        <div className="pt-3 border-t border-border-light">
          <div className="mono-label text-black/50 mb-3">Billing rates (USD/hour)</div>
          <p className="font-display text-caption text-black/60 mb-4 tracking-body">
            Set <strong>Standard</strong> to bill wall-clock hours at one rate. Leave Standard
            empty and set <strong>Human</strong> / <strong>Agent</strong> to bill them
            separately. Empty fields inherit from the client if one is linked.
          </p>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Standard" compact>
              <input
                type="number"
                min={0}
                step="0.01"
                value={standardRate}
                onChange={(e) => setStandardRate(e.target.value)}
                placeholder="—"
                className="input"
              />
            </Field>
            <Field label="Human" compact>
              <input
                type="number"
                min={0}
                step="0.01"
                value={humanRate}
                onChange={(e) => setHumanRate(e.target.value)}
                placeholder="—"
                className="input"
              />
            </Field>
            <Field label="Agent" compact>
              <input
                type="number"
                min={0}
                step="0.01"
                value={agentRate}
                onChange={(e) => setAgentRate(e.target.value)}
                placeholder="—"
                className="input"
              />
            </Field>
          </div>
        </div>

        {error && (
          <p className="font-display text-caption text-[#b91c1c]">{error}</p>
        )}
      </form>
    </Modal>
  );
}

function Field({
  label,
  hint,
  compact,
  children,
}: {
  label: string;
  hint?: string;
  compact?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className={`block ${compact ? '' : ''}`}>
      <div className="mono-label text-black/50 mb-2">{label}</div>
      {children}
      {hint && <p className="font-display text-caption text-black/50 mt-1">{hint}</p>}
    </label>
  );
}
